import type { DeviceFirmwareUpdate } from 'homey-api';
import { createContext, useContext, useEffect, useEffectEvent, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ToastManager } from '../../../../ToastManager.jsx';
import { useRowContext } from '../SettingsTable';

import { devicesStore } from '../../../../store/devices/DevicesStore.ts';
import type { DeviceProps } from '../../../../store/devices/DevicesStore.ts';

import { useDevice } from '../../../../store/devices/useDevices.ts';
import { useApi } from '../../../../store/SelectedHomeyStore.hooks';

type DeviceUpdatesSettingsTableRowContextValue = {
  progress: number | null;
};

const DeviceUpdatesSettingsTableRowContext =
  createContext<DeviceUpdatesSettingsTableRowContextValue>({
    progress: null,
  });

export function useDeviceUpdatesSettingsTableRowContext() {
  return useContext(DeviceUpdatesSettingsTableRowContext);
}

interface DeviceUpdatesSettingsTableRowProps {
  children: ReactNode;
}

export function DeviceUpdatesSettingsTableRow(props: DeviceUpdatesSettingsTableRowProps) {
  const { row } = useRowContext<DeviceProps>();
  const { device, deviceInstance } = useDevice({ deviceId: String(row.key) });
  const { api } = useApi();
  const [progress, setProgress] = useState<number | null>(device?.firmwareUpdate?.progress ?? null);

  useEffect(() => {
    setProgress(device?.firmwareUpdate?.progress ?? null);
  }, [device?.firmwareUpdate?.progress]);

  const listener = useEffectEvent((state: Partial<DeviceFirmwareUpdate>) => {
    if (device == null) {
      return;
    }

    const hasProgressUpdate = Object.prototype.hasOwnProperty.call(state, 'progress');

    if (hasProgressUpdate) {
      setProgress(state.progress ?? null);
    }

    if (hasProgressUpdate && Object.keys(state).length === 1) {
      return;
    }

    devicesStore.updateOneOptimistic(device.id, {
      firmwareUpdate: {
        ...device.firmwareUpdate,
        ...state,
      },
    });
  });

  useEffect(() => {
    if (deviceInstance == null || api == null) {
      return;
    }

    const reference = {};

    deviceInstance.on('firmware-update-state', listener);

    devicesStore
      .connectItem({
        api,
        item: deviceInstance,
        reference,
      })
      .catch(ToastManager.handleError);

    return function cleanup() {
      deviceInstance.off('firmware-update-state', listener);
      devicesStore
        .disconnectItem({
          api,
          item: deviceInstance,
          reference,
        })
        .catch(ToastManager.handleError);
    };
  }, [deviceInstance, api]);

  const contextValue = useMemo(
    () => ({
      progress,
    }),
    [progress],
  );

  return (
    <DeviceUpdatesSettingsTableRowContext.Provider value={contextValue}>
      {props.children}
    </DeviceUpdatesSettingsTableRowContext.Provider>
  );
}
